<template>
  <div class="help-root">
    <!-- Top bar -->
    <header class="help-topbar">
      <button
        class="help-icon-btn help-menu-toggle"
        type="button"
        aria-label="Toggle contents"
        @click="sidebarOpen = !sidebarOpen"
      >
        <i class="mdi mdi-menu"></i>
      </button>
      <router-link class="help-brand" :to="{ name: 'app-admin-help' }">
        <i class="mdi mdi-book-open-page-variant-outline"></i>
        <span>User Manual</span>
      </router-link>
      <button class="help-back" type="button" @click="exitToApp">
        <i class="mdi mdi-arrow-left"></i>
        <span>Back to app</span>
      </button>
      <div class="help-search">
        <i class="mdi mdi-magnify"></i>
        <input
          ref="search"
          v-model="query"
          type="search"
          placeholder="Search the manual"
          aria-label="Search the manual"
          @focus="searchFocused = true"
          @blur="onSearchBlur"
        />
        <ul v-if="searchFocused && query.trim()" class="help-search-results">
          <li v-if="!results.length" class="help-search-empty">No matches</li>
          <li v-for="r in results" :key="r.domain + '/' + r.slug">
            <a href="#" @mousedown.prevent="goto(r.domain, r.slug)">
              <strong>{{ r.title }}</strong>
              <span>{{ r.sectionTitle }}</span>
            </a>
          </li>
        </ul>
      </div>
    </header>

    <div class="help-body">
      <!-- Sidebar -->
      <nav
        class="help-sidebar"
        :class="{ 'is-open': sidebarOpen }"
        aria-label="Manual contents"
      >
        <div class="help-sidebar-inner">
          <button class="help-nav-home help-nav-back" type="button" @click="exitToApp">
            <i class="mdi mdi-arrow-left"></i> Back to app
          </button>
          <router-link
            class="help-nav-home"
            :to="{ name: 'app-admin-help' }"
            :class="{ active: !isArticle }"
          >
            <i class="mdi mdi-home-outline"></i> Manual home
          </router-link>

          <div v-for="section in manifest" :key="section.domain" class="help-section">
            <button
              class="help-section-head"
              type="button"
              @click="toggleSection(section.domain)"
            >
              <i
                class="mdi"
                :class="
                  collapsed[section.domain]
                    ? 'mdi-chevron-right'
                    : 'mdi-chevron-down'
                "
              ></i>
              {{ section.title }}
            </button>
            <ul v-show="!collapsed[section.domain]" class="help-page-list">
              <li v-for="page in visiblePages(section)" :key="page.slug">
                <router-link
                  :to="articleTo(section.domain, page.slug)"
                  :class="{ active: isCurrent(section.domain, page.slug) }"
                >
                  {{ page.title }}
                  <span v-if="page.draft" class="help-draft-tag">draft</span>
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      <div
        v-if="sidebarOpen"
        class="help-scrim"
        @click="sidebarOpen = false"
      ></div>

      <!-- Main -->
      <main class="help-main">
        <!-- Landing -->
        <div v-if="!isArticle" class="help-landing">
          <h1>User Manual</h1>
          <p class="help-lede">
            Step-by-step guidance for the day-to-day screens in this system.
            Pick a topic below or use the search box above.
          </p>
          <div class="help-cards">
            <router-link
              v-for="section in manifest"
              :key="section.domain"
              class="help-card"
              :to="firstArticleTo(section)"
            >
              <h3>{{ section.title }}</h3>
              <ul>
                <li v-for="page in visiblePages(section).slice(0, 4)" :key="page.slug">
                  {{ page.title }}
                </li>
              </ul>
              <span class="help-card-count">
                {{ visiblePages(section).length }} page{{
                  visiblePages(section).length === 1 ? "" : "s"
                }}
              </span>
            </router-link>
          </div>
        </div>

        <!-- Article -->
        <article v-else class="help-article">
          <nav class="help-breadcrumb">
            <router-link :to="{ name: 'app-admin-help' }">Manual</router-link>
            <i class="mdi mdi-chevron-right"></i>
            <span v-if="currentSection">{{ currentSection.title }}</span>
            <i class="mdi mdi-chevron-right"></i>
            <span class="help-crumb-current">{{ currentTitle }}</span>
          </nav>

          <div
            v-if="articleHtml"
            ref="content"
            class="help-markdown"
            v-html="articleHtml"
          ></div>
          <div v-else class="help-notfound">
            <h1>Page not found</h1>
            <p>
              This manual page doesn't exist yet.
              <router-link :to="{ name: 'app-admin-help' }">Back to the manual home</router-link>.
            </p>
          </div>

          <nav v-if="articleHtml" class="help-pager">
            <router-link
              v-if="pager.prev"
              class="help-pager-prev"
              :to="articleTo($route.params.domain, pager.prev.slug)"
            >
              <span>Previous</span>
              <strong>{{ pager.prev.title }}</strong>
            </router-link>
            <span v-else></span>
            <router-link
              v-if="pager.next"
              class="help-pager-next"
              :to="articleTo($route.params.domain, pager.next.slug)"
            >
              <span>Next</span>
              <strong>{{ pager.next.title }}</strong>
            </router-link>
          </nav>
        </article>
      </main>
    </div>

    <!-- Lightbox -->
    <div v-if="lightbox" class="help-lightbox" @click="lightbox = null">
      <button class="help-icon-btn help-lightbox-close" aria-label="Close">
        <i class="mdi mdi-close"></i>
      </button>
      <img :src="lightbox" alt="" />
    </div>
  </div>
</template>

<script>
import {
  visibleManifest,
  flatPages,
  findPage,
} from "@/help/manifest";
import { renderPage, pageHeading } from "@/help/content";

export default {
  name: "AdminHelpManual",
  data() {
    return {
      manifest: visibleManifest(),
      flat: flatPages(),
      collapsed: {},
      query: "",
      searchFocused: false,
      sidebarOpen: false,
      lightbox: null,
    };
  },
  computed: {
    isArticle() {
      return !!this.$route.params.slug;
    },
    isCurrentRouteArticle() {
      return this.$route.name === "app-admin-help-article";
    },
    currentSection() {
      const d = this.$route.params.domain;
      return this.manifest.find((s) => s.domain === d) || null;
    },
    currentTitle() {
      const { domain, slug } = this.$route.params;
      return (
        pageHeading(domain, slug) ||
        (findPage(domain, slug) && findPage(domain, slug).page.title) ||
        slug
      );
    },
    articleHtml() {
      const { domain, slug } = this.$route.params;
      if (!domain || !slug) return null;
      return renderPage(domain, slug);
    },
    pager() {
      const { domain, slug } = this.$route.params;
      const f = findPage(domain, slug);
      return {
        prev: f ? f.prev : null,
        next: f ? f.next : null,
      };
    },
    results() {
      const q = this.query.trim().toLowerCase();
      if (!q) return [];
      return this.flat
        .filter(
          (p) =>
            p.title.toLowerCase().includes(q) ||
            p.sectionTitle.toLowerCase().includes(q)
        )
        .slice(0, 12);
    },
  },
  watch: {
    $route() {
      this.sidebarOpen = false;
      this.$nextTick(this.afterRender);
      window.scrollTo(0, 0);
    },
  },
  created() {
    // Collapse every section except the one being viewed.
    const open = this.$route.params.domain;
    this.manifest.forEach((s) => {
      this.$set(this.collapsed, s.domain, s.domain !== open);
    });
  },
  mounted() {
    this.$nextTick(this.afterRender);
  },
  methods: {
    isArticleRoute() {
      return !!this.$route.params.slug;
    },
    visiblePages(section) {
      return section.pages.filter((p) => !p.draft);
    },
    articleTo(domain, slug) {
      return { name: "app-admin-help-article", params: { domain, slug } };
    },
    firstArticleTo(section) {
      const p = this.visiblePages(section)[0] || section.pages[0];
      return this.articleTo(section.domain, p.slug);
    },
    isCurrent(domain, slug) {
      return (
        this.$route.params.domain === domain &&
        this.$route.params.slug === slug
      );
    },
    toggleSection(domain) {
      this.$set(this.collapsed, domain, !this.collapsed[domain]);
    },
    goto(domain, slug) {
      this.query = "";
      this.searchFocused = false;
      this.$router.push(this.articleTo(domain, slug)).catch(() => {});
    },
    exitToApp() {
      this.$router.push("/app/admin/dashboard").catch(() => {});
    },
    onSearchBlur() {
      // Delay so a result click registers first.
      setTimeout(() => {
        this.searchFocused = false;
      }, 150);
    },
    afterRender() {
      const el = this.$refs.content;
      if (!el) return;
      el.querySelectorAll("a[data-help-link]").forEach((a) => {
        a.addEventListener("click", this.onInContentLink);
      });
      el.querySelectorAll("img.help-shot").forEach((img) => {
        img.addEventListener("click", this.onShotClick);
      });
    },
    onInContentLink(ev) {
      const href = ev.currentTarget.getAttribute("href");
      if (!href) return;
      ev.preventDefault();
      const target = this.resolveMdHref(href);
      if (target) this.$router.push(target).catch(() => {});
    },
    resolveMdHref(href) {
      // Strip any #hash, drop .md
      const [pathPart] = href.split("#");
      const clean = pathPart.replace(/\.md$/, "").replace(/^\.\//, "");
      const curDomain = this.$route.params.domain;
      let domain;
      let slug;
      if (clean.indexOf("/") !== -1) {
        // "domain/slug" or "../domain/slug"
        const parts = clean.split("/").filter((p) => p && p !== "..");
        slug = parts.pop();
        domain = parts.pop() || curDomain;
      } else {
        // bare sibling "slug"
        domain = curDomain;
        slug = clean;
      }
      return findPage(domain, slug) ? this.articleTo(domain, slug) : null;
    },
    onShotClick(ev) {
      const full = ev.currentTarget.getAttribute("data-full");
      if (full) this.lightbox = full;
    },
  },
};
</script>

<style scoped>
.help-root {
  --help-primary: #f07025;
  --help-primary-dark: #c9591a;
  --help-ink: #24292f;
  --help-muted: #57606a;
  --help-line: #e3e6ea;
  --help-bg: #ffffff;
  --help-bg-soft: #f6f7f9;
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
  color: var(--help-ink);
  background: var(--help-bg);
  min-height: 100%;
}

.help-topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 18px;
  background: var(--help-bg);
  border-bottom: 1px solid var(--help-line);
}
.help-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 16px;
  color: var(--help-ink);
  text-decoration: none;
}
.help-brand .mdi {
  color: var(--help-primary);
  font-size: 22px;
}
.help-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
  padding: 6px 12px;
  border: 1px solid var(--help-line);
  border-radius: 8px;
  background: var(--help-bg);
  color: var(--help-muted);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.help-back:hover {
  color: var(--help-primary-dark);
  border-color: var(--help-primary);
}
.help-back .mdi {
  font-size: 16px;
}
.help-icon-btn {
  border: 0;
  background: transparent;
  cursor: pointer;
  color: var(--help-muted);
  font-size: 22px;
  line-height: 1;
  padding: 4px;
  border-radius: 6px;
}
.help-icon-btn:hover {
  background: var(--help-bg-soft);
}
.help-menu-toggle {
  display: none;
}

.help-search {
  position: relative;
  margin-left: auto;
  width: min(340px, 42vw);
}
.help-search .mdi {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--help-muted);
  font-size: 18px;
}
.help-search input {
  width: 100%;
  padding: 8px 12px 8px 34px;
  border: 1px solid var(--help-line);
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  background: var(--help-bg-soft);
}
.help-search input:focus {
  border-color: var(--help-primary);
  background: var(--help-bg);
}
.help-search-results {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--help-bg);
  border: 1px solid var(--help-line);
  border-radius: 8px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 6px;
  max-height: 60vh;
  overflow: auto;
  z-index: 30;
}
.help-search-results a {
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  border-radius: 6px;
  text-decoration: none;
  color: var(--help-ink);
}
.help-search-results a:hover {
  background: var(--help-bg-soft);
}
.help-search-results span {
  font-size: 12px;
  color: var(--help-muted);
}
.help-search-empty {
  padding: 10px;
  color: var(--help-muted);
  font-size: 13px;
}

.help-body {
  display: flex;
  align-items: flex-start;
}

.help-sidebar {
  flex: 0 0 288px;
  width: 288px;
  position: sticky;
  top: 57px;
  align-self: flex-start;
  height: calc(100vh - 57px);
  overflow-y: auto;
  border-right: 1px solid var(--help-line);
  background: var(--help-bg-soft);
}
.help-sidebar-inner {
  padding: 14px 10px 40px;
}
.help-nav-home {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  color: var(--help-muted);
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
}
.help-nav-home.active,
.help-nav-home:hover {
  color: var(--help-primary-dark);
  background: rgba(240, 112, 37, 0.08);
}
.help-nav-back {
  width: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  margin-bottom: 2px;
}
.help-section {
  margin-top: 6px;
}
.help-section-head {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: var(--help-ink);
  padding: 8px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.help-page-list {
  list-style: none;
  margin: 0 0 4px;
  padding: 0 0 0 6px;
}
.help-page-list a {
  display: block;
  padding: 6px 10px;
  margin: 1px 0;
  font-size: 13px;
  color: var(--help-muted);
  text-decoration: none;
  border-radius: 6px;
  border-left: 2px solid transparent;
}
.help-page-list a:hover {
  color: var(--help-ink);
  background: rgba(0, 0, 0, 0.04);
}
.help-page-list a.active {
  color: var(--help-primary-dark);
  background: rgba(240, 112, 37, 0.1);
  border-left-color: var(--help-primary);
  font-weight: 600;
}
.help-draft-tag {
  font-size: 10px;
  background: #ddd;
  color: #333;
  border-radius: 4px;
  padding: 0 4px;
  margin-left: 4px;
  vertical-align: middle;
}

.help-main {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 28px clamp(18px, 4vw, 56px) 80px;
}
.help-article {
  max-width: 1040px;
}
.help-landing {
  max-width: 1280px;
}

.help-landing h1 {
  margin: 0 0 6px;
  font-size: 30px;
}
.help-lede {
  color: var(--help-muted);
  font-size: 15px;
  max-width: 60ch;
  margin: 0 0 26px;
}
.help-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}
.help-card {
  display: block;
  border: 1px solid var(--help-line);
  border-radius: 12px;
  padding: 16px 18px;
  text-decoration: none;
  color: var(--help-ink);
  background: var(--help-bg);
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.help-card:hover {
  border-color: var(--help-primary);
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.help-card h3 {
  margin: 0 0 8px;
  font-size: 16px;
  color: var(--help-primary-dark);
}
.help-card ul {
  margin: 0;
  padding-left: 16px;
  font-size: 13px;
  color: var(--help-muted);
}
.help-card-count {
  display: inline-block;
  margin-top: 10px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--help-muted);
}

.help-breadcrumb {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--help-muted);
  margin-bottom: 18px;
}
.help-breadcrumb a {
  color: var(--help-muted);
  text-decoration: none;
}
.help-breadcrumb a:hover {
  color: var(--help-primary-dark);
}
.help-breadcrumb .mdi {
  font-size: 14px;
}
.help-crumb-current {
  color: var(--help-ink);
  font-weight: 600;
}

.help-pager {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  margin-top: 46px;
  padding-top: 20px;
  border-top: 1px solid var(--help-line);
}
.help-pager a {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--help-line);
  border-radius: 10px;
  padding: 12px 16px;
  text-decoration: none;
  color: var(--help-ink);
  max-width: 48%;
}
.help-pager a:hover {
  border-color: var(--help-primary);
}
.help-pager-next {
  text-align: right;
}
.help-pager span {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--help-muted);
}
.help-pager strong {
  font-size: 14px;
  margin-top: 2px;
}

.help-scrim {
  display: none;
}

.help-lightbox {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4vh 4vw;
  cursor: zoom-out;
}
.help-lightbox img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 6px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);
}
.help-lightbox-close {
  position: absolute;
  top: 16px;
  right: 20px;
  color: #fff;
  font-size: 28px;
}

.help-notfound {
  padding: 40px 0;
}

@media (max-width: 960px) {
  .help-menu-toggle {
    display: inline-flex;
  }
  .help-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 50;
    transform: translateX(-100%);
    transition: transform 0.2s ease;
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.2);
  }
  .help-sidebar.is-open {
    transform: translateX(0);
  }
  .help-scrim {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 40;
    background: rgba(0, 0, 0, 0.35);
  }
  .help-search {
    width: 40vw;
  }
  .help-back span {
    display: none;
  }
  .help-back {
    padding: 6px 8px;
  }
}
</style>

<!-- Unscoped: styling for the rendered Markdown body only. -->
<style>
.help-markdown {
  max-width: 100%;
  font-size: 15px;
  line-height: 1.7;
  color: #24292f;
}
.help-markdown p,
.help-markdown li,
.help-markdown blockquote {
  max-width: 76ch;
}
.help-markdown > *:first-child {
  margin-top: 0;
}
.help-markdown h1 {
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f07025;
}
.help-markdown h2 {
  font-size: 21px;
  margin: 36px 0 12px;
}
.help-markdown h3 {
  font-size: 17px;
  margin: 26px 0 10px;
}
.help-markdown h4 {
  font-size: 15px;
  margin: 20px 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #57606a;
}
.help-markdown p {
  margin: 0 0 14px;
}
.help-markdown ul,
.help-markdown ol {
  margin: 0 0 14px;
  padding-left: 24px;
}
.help-markdown li {
  margin: 4px 0;
}
.help-markdown a {
  color: #c9591a;
  text-decoration: none;
}
.help-markdown a:hover {
  text-decoration: underline;
}
.help-markdown code {
  background: #f0f2f4;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.9em;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
}
.help-markdown pre {
  background: #f6f7f9;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  padding: 14px 16px;
  overflow-x: auto;
}
.help-markdown pre code {
  background: transparent;
  padding: 0;
}
.help-markdown blockquote {
  margin: 0 0 16px;
  padding: 4px 16px;
  border-left: 4px solid #f07025;
  background: #fff7f2;
  color: #57606a;
}
.help-markdown table {
  border-collapse: collapse;
  width: 100%;
  margin: 0 0 18px;
  font-size: 14px;
  display: block;
  overflow-x: auto;
}
.help-markdown th,
.help-markdown td {
  border: 1px solid #e3e6ea;
  padding: 8px 12px;
  text-align: left;
  vertical-align: top;
}
.help-markdown th {
  background: #f6f7f9;
  font-weight: 700;
}
.help-markdown img.help-shot {
  display: block;
  max-width: 100%;
  margin: 18px 0;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  cursor: zoom-in;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}
.help-markdown .help-shot-pending {
  display: block;
  margin: 18px 0;
  padding: 22px 16px;
  border: 1px dashed #c7ccd1;
  border-radius: 8px;
  background: repeating-linear-gradient(
    45deg,
    #fafafa,
    #fafafa 10px,
    #f2f2f2 10px,
    #f2f2f2 20px
  );
  color: #57606a;
  font-size: 13px;
  text-align: center;
}
.help-markdown hr {
  border: 0;
  border-top: 1px solid #e3e6ea;
  margin: 28px 0;
}
</style>
